/**
 * MetricExplanationContentContainer
 *
 * Container component for displaying metric explanation content with charts and descriptions.
 * Handles data preparation, chart data computation, and font/screen size hooks.
 * @remarks
 * This component orchestrates the display of metric explanations including:
 * - Metric descriptions with team comparisons
 * - Methodology explanations
 * - DotPlot visualizations for most metrics
 * - CategoryMetricChart visualizations for specific categorical metrics
 *
 * The component computes category chart data using useMemo for metrics like
 * carrying, dribbling, passing, shooting, shot_stopping, and gk_passing.
 */

import { useMemo } from "react";

import MetricExplanationContentView from "./MetricExplanationContentView";
import {
  CATEGORICAL_METRIC_SCORE_KEYS,
  isCategoricalMetricId,
} from "@/constants/metrics";

import { CategoryMetricChartProps } from "@/components/organisms/CategoryMetricChart/CategoryMetricChartContainer";
import useMobileScreen from "@/hooks/useMobileScreen";
import { useSafeFont } from "@/hooks/useSafeFont";
import {
  MetricExplanationContentProps,
  RosterDataState,
} from "@/types/metricExplanationTypes";
import {
  cleanSeasonScores,
  getCategoryMetric,
} from "@/utils/handleDataCategoryMetrics";

/**
 * Container component for metric explanation content
 * @param props - Component props including explanation, metric values, player data, and theme
 * @param props.explanation - Metric explanation object containing title, description, and methodology
 * @param props.metricValue - Numeric or string value of the metric to display
 * @param props.rosterValues - Array of metric values for team roster comparison
 * @param props.player - Player data including positions and categorical scores
 * @param props.listPlayerSameTeamAndPosition - Array of players in same team and position for comparison
 * @param props.isFetchingListPlayerSameTeamAndPosition - Whether comparison players data is loading
 * @param props.isDark - Whether dark theme is active (affects text and chart colors)
 */
function MetricExplanationContentContainer({
  explanation,
  metricValue,
  rosterValues,
  player,
  listPlayerSameTeamAndPosition,
  isFetchingListPlayerSameTeamAndPosition,
  isDark,
}: MetricExplanationContentProps) {
  const score =
    typeof metricValue === "number"
      ? Math.round(metricValue)
      : Math.round(Number(metricValue));
  const hasScore =
    metricValue !== null && metricValue !== undefined && !Number.isNaN(score);
  const hasRosterData = rosterValues && rosterValues.length > 0;
  const teamAverage = hasRosterData
    ? Math.round(rosterValues.reduce((a, b) => a + b, 0) / rosterValues.length)
    : undefined;

  const font = useSafeFont(12);
  const isSmallScreen = useMobileScreen();

  const playerPositions = player?.positions
    ?.map(p => p?.abbreviation)
    .join(", ");
  const playerName = player?.name ?? "This Player";

  const categoryChartData = useMemo<{
    data: CategoryMetricChartProps["data"];
    yKeys: string[];
    colors: Record<string, string>;
  } | null>(() => {
    if (!player || !listPlayerSameTeamAndPosition) return null;
    if (!isCategoricalMetricId(explanation.id)) return null;

    const cleanedSeasonCategoricalScores = cleanSeasonScores(
      player?.seasonCategoricalScores
    );

    /*
     * SE-7449: the score key comes from the shared
     * `CATEGORICAL_METRIC_SCORE_KEYS` map rather than from a `switch` that
     * repeated the same six ids. The old shape let the switch and
     * `CATEGORICAL_METRIC_IDS` drift apart, and a metric present in only one of
     * them rendered a roster DotPlot AND no category chart.
     */
    return (
      getCategoryMetric(
        cleanedSeasonCategoricalScores,
        listPlayerSameTeamAndPosition,
        player.id,
        CATEGORICAL_METRIC_SCORE_KEYS[explanation.id]
      ) ?? null
    );
  }, [explanation.id, player, listPlayerSameTeamAndPosition]);

  const fullDescription = useMemo(
    () =>
      hasScore
        ? explanation.description(score, teamAverage) +
          (teamAverage !== undefined && explanation.teamComparison
            ? ` ${explanation.teamComparison(score, teamAverage)}`
            : "")
        : "No score available for this metric.",
    [hasScore, explanation, score, teamAverage]
  );

  const roundedRosterValues = useMemo(
    () =>
      hasRosterData ? rosterValues.map(val => Math.round(val)) : undefined,
    [hasRosterData, rosterValues]
  );

  /*
   * SE-7394: the DotPlot used to render only when roster values were present,
   * so an empty or still-in-flight roster comparison produced NO chart, NO
   * spinner and NO empty state — indistinguishable from a metric that has no
   * chart by design. Collapse that into one explicit four-state decision.
   *
   * `ready` deliberately wins over `loading` — DO NOT reorder these two checks.
   * SE-7394 justified that precedence by saying roster values were snapshotted
   * when the modal opened; SE-7447 removed the snapshot, so callers now derive
   * `rosterValues` live from the query. The precedence rule survives that
   * change and became MORE load-bearing, not less:
   *
   *   - It is what promotes an open modal from spinner to chart when a
   *     still-in-flight roster query resolves. That IS the SE-7447 fix; check
   *     `isFetching` first and the bug comes straight back.
   *   - It still stops a background re-fetch from blanking a chart the user is
   *     already reading, which now rests on Apollo retaining `data` for
   *     unchanged variables rather than on a local snapshot.
   */
  const rosterDataState: RosterDataState = useMemo(() => {
    const metricHasDotPlot = hasScore && !isCategoricalMetricId(explanation.id);

    if (!metricHasDotPlot) return "hidden";
    if (hasRosterData) return "ready";
    return isFetchingListPlayerSameTeamAndPosition ? "loading" : "empty";
  }, [
    hasScore,
    explanation.id,
    hasRosterData,
    isFetchingListPlayerSameTeamAndPosition,
  ]);

  /*
   * SE-7449: the categorical over-time chart had the SAME silent-blank shape
   * SE-7394 removed from the DotPlot region next door. The view gated it on
   * `categoryChartData && categoryChartData.data.length > 0`, and
   * `categoryChartData` is null for the whole window in which
   * `PlayerDetailListPlayersByTeamIdsAndPositionIds` is in flight — which is
   * every cold load, since that query only starts once the player resolves and
   * every metric tile is tappable before it does. The branch fell through to
   * nothing: no chart, no skeleton, no empty state.
   *
   * `CategoryMetricChart` does carry its own `loading` affordance, but it was
   * unreachable from this call site for exactly the same reason — the gate that
   * decides whether to mount it is false precisely when loading is true.
   *
   * The precedence rule is inherited deliberately from the DotPlot region:
   * `ready` wins over `loading`, so an open modal is promoted from spinner to
   * chart when the peer query resolves, and a background re-fetch cannot blank
   * a chart the user is already reading.
   */
  const categoryChartDataState: RosterDataState = useMemo(() => {
    if (!isCategoricalMetricId(explanation.id)) return "hidden";
    if ((categoryChartData?.data?.length ?? 0) > 0) return "ready";
    return isFetchingListPlayerSameTeamAndPosition ? "loading" : "empty";
  }, [
    explanation.id,
    categoryChartData,
    isFetchingListPlayerSameTeamAndPosition,
  ]);

  return (
    <MetricExplanationContentView
      explanation={explanation}
      fullDescription={fullDescription}
      hasScore={hasScore}
      score={score}
      roundedRosterValues={roundedRosterValues}
      rosterDataState={rosterDataState}
      player={player}
      isDark={isDark}
      categoryChartData={categoryChartData}
      categoryChartDataState={categoryChartDataState}
      playerPositions={playerPositions}
      font={font}
      playerName={playerName}
      isSmallScreen={isSmallScreen}
    />
  );
}

export default MetricExplanationContentContainer;
